import { useState, useEffect, useRef } from "react";
import { T } from "../../config/theme.js";
import { api, trackEvent } from "../../hooks/useApi.js";
import { Btn, Input } from "../ui/index.jsx";

export default function MarketQA({ token, onBack, plan }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("시장 분석 중...");
  const [showHistory, setShowHistory] = useState(false);
  const bottom = useRef(null);

  useEffect(() => {
    api("/qa/sessions", {}, token).then(list => {
      const filtered = (list || []).filter(s => s.symbol === "MARKET");
      setSessions(filtered);
      if (filtered.length === 0) newSession();
    }).catch(() => newSession());
  }, []);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // Sprint 12: Breathing 로딩 텍스트 전환
  useEffect(() => {
    let t1, t2;
    if (loading) {
      setLoadingText("시장 데이터 수집 중...");
      t1 = setTimeout(() => setLoadingText("글로벌 지표 확인 중..."), 1500);
      t2 = setTimeout(() => setLoadingText("포트폴리오 영향 분석 중..."), 3500);
    }
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [loading]);

  const newSession = async () => {
    try {
      const s = await api("/qa/sessions", { method: "POST", body: JSON.stringify({ symbol: "MARKET", title: "시장 전체 상담" }) }, token);
      setSid(s.id);
      setMsgs([]);
      setShowHistory(false);
      api("/qa/sessions", {}, token).then(list => setSessions((list || []).filter(ss => ss.symbol === "MARKET"))).catch(() => {});
    } catch (e) { console.error(e); }
  };

  const loadSession = async (sessionId) => {
    setSid(sessionId);
    setShowHistory(false);
    try {
      const messages = await api(`/qa/sessions/${sessionId}/messages`, {}, token);
      const parsed = (messages || []).map(m => {
        if (m.role === "user") return { role: "user", text: typeof m.content === "string" ? m.content : m.content };
        // Sprint 11: 과거 메시지도 정규화
        let data;
        try {
          const raw = typeof m.content === "string" ? JSON.parse(m.content) : m.content;
          data = (typeof raw === "object" && raw !== null) ? raw : { one_liner: "", answer: String(raw) };
        } catch {
          data = { one_liner: "답변 확인", answer: typeof m.content === "string" ? m.content : String(m.content) };
        }
        return { role: "ai", data };
      });
      setMsgs(parsed);
    } catch (e) { setMsgs([]); }
  };

  const send = async (overrideText = null) => {
    const override = (typeof overrideText === "string") ? overrideText : null;
    const q = (override || input).trim();
    if (!q || loading) return;
    let currentSid = sid;
    if (!currentSid) {
      try {
        const s = await api("/qa/sessions", { method: "POST", body: JSON.stringify({ symbol: "MARKET", title: "시장 전체 상담" }) }, token);
        if (s?.id) { setSid(s.id); currentSid = s.id; } else return;
      } catch { return; }
    }
    setInput(""); setMsgs(p => [...p, { role: "user", text: q }]); setLoading(true);
    try {
      trackEvent("qa.ask", { symbol: "MARKET", meta: { session_id: currentSid } });
      const r = await api(`/qa/sessions/${currentSid}/messages`, { method: "POST", body: JSON.stringify({ question: q, symbol: "MARKET" }) }, token);
      // Sprint 12: answer 정규화 — 잘린 JSON도 안전 처리
      const normalizeAnswer = (ans) => {
        if (!ans) return { one_liner: "답변 없음", answer: "답변을 받지 못했어요." };
        if (typeof ans === "object" && ans.answer) return ans;
        if (typeof ans === "object") return { one_liner: ans.one_liner || "", answer: ans.answer || JSON.stringify(ans) };
        if (typeof ans === "string") {
          const s = ans.trim();
          // 정상 JSON 파싱 시도
          if (s.startsWith("{")) {
            try { return JSON.parse(s); } catch {
              // 잘린 JSON → answer 필드만 추출 시도
              const olMatch = s.match(/"one_liner"\s*:\s*"([^"]*)"/);
              const anMatch = s.match(/"answer"\s*:\s*"([\s\S]*)/);
              if (anMatch) {
                let answer = anMatch[1];
                // 닫히지 않은 따옴표/중괄호 정리
                answer = answer.replace(/",?\s*"conditions"[\s\S]*$/, "").replace(/"\s*}?\s*$/, "").replace(/\\n/g, "\n");
                return { one_liner: olMatch ? olMatch[1] : "답변 확인", answer: answer || s };
              }
              // 추출 실패 → 전체를 answer로
              return { one_liner: "답변 확인", answer: s.replace(/[{}"\n]/g, " ").replace(/one_liner:|answer:/g, "").trim() };
            }
          }
          return { one_liner: "답변 확인", answer: s };
        }
        return { one_liner: "답변 확인", answer: String(ans) };
      };
      setMsgs(p => [...p, { role: "ai", data: normalizeAnswer(r.answer), usedWebSearch: r._used_web_search || false }]);
    } catch (e) { setMsgs(p => [...p, { role: "ai", data: { one_liner: "오류", answer: e.message } }]); }
    setLoading(false);
  };

  const suggestions = [
    "지금 시장 전체적으로 어떤 상황이야?",
    "요즘 주목할 섹터가 있어?",
    "환율이 계속 오르면 어떤 업종에 영향이 있어?",
    "외국인이 요즘 어떤 흐름이야?",
    "금리 변동이 주식시장에 미치는 영향은?",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Btn variant="ghost" style={{ padding: "6px 12px" }} onClick={onBack}>←</Btn>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>🌐 시장 전체 질문</div>
            <div style={{ fontSize: 11, color: T.textDim }}>거시 경제 · 섹터 · 시장 흐름 · AI 상담</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? "✕ 닫기" : `📋 기록 (${sessions.length})`}
          </Btn>
          <Btn variant="small" onClick={newSession}>+ 새 대화</Btn>
        </div>
      </div>

      {/* Session History Panel */}
      {showHistory && (
        <div style={{ marginBottom: 14, maxHeight: 200, overflowY: "auto", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg2 }}>
          {sessions.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: T.textMuted }}>아직 대화 기록이 없어요</div>
          ) : sessions.map(s => (
            <div key={s.id} onClick={() => loadSession(s.id)} style={{
              padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${T.border}`,
              background: s.id === sid ? T.cyan + "15" : "transparent", transition: "background 0.15s",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
              onMouseEnter={e => { if (s.id !== sid) e.currentTarget.style.background = T.cardHover; }}
              onMouseLeave={e => { if (s.id !== sid) e.currentTarget.style.background = "transparent"; }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.id === sid ? T.cyan : T.text }}>{s.title || "시장 전체 상담"}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{new Date(s.created_at).toLocaleDateString("ko-KR")} {new Date(s.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              {s.id === sid && <span style={{ fontSize: 10, color: T.cyan, fontWeight: 700 }}>현재</span>}
            </div>
          ))}
        </div>
      )}

      {/* Chat Area */}
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 14, paddingRight: 4 }}>
        {msgs.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: T.textDim }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>🌐</div>
            <div style={{ fontSize: 15, marginBottom: 6, color: T.text, fontWeight: 600 }}>시장 전체에 대해 물어보세요</div>
            <div style={{ fontSize: 12, marginBottom: 20, lineHeight: 1.6, color: T.textMuted }}>
              지수, 환율, 금리, 외국인 흐름, 섹터 동향 등<br />
              종목이 아닌 시장 전체에 대한 질문을 할 수 있어요
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 380, margin: "0 auto" }}>
              {suggestions.map((s, i) => (
                <div key={i} onClick={() => { setInput(s); }} style={{
                  padding: "10px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10,
                  fontSize: 13, color: T.textDim, cursor: "pointer", textAlign: "left", transition: "all 0.2s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent + "60"; e.currentTarget.style.color = T.text; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textDim; }}
                >💡 {s}</div>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "80%", padding: "14px 18px", borderRadius: 14, background: m.role === "user" ? T.accent : T.card, border: m.role === "user" ? "none" : `1px solid ${T.border}`, color: T.text, fontSize: 14, lineHeight: 1.7 }}>
              {m.role === "user" ? m.text : (() => {
                // Sprint 11: safeData — 문자열/객체 모두 안전 처리
                const d = (() => {
                  if (!m.data) return { one_liner: "", answer: "" };
                  if (typeof m.data === "object") return m.data;
                  if (typeof m.data === "string") {
                    try { return JSON.parse(m.data); } catch { return { one_liner: "답변 확인", answer: m.data }; }
                  }
                  return { one_liner: "", answer: String(m.data) };
                })();
                return (<div>
                  {d?.one_liner && <div style={{ fontWeight: 700, color: T.cyan, marginBottom: 8, fontSize: 15 }}>
                    🌐 {d.one_liner}
                    {m.usedWebSearch && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 8px", background: T.cyan + "20", color: T.cyan, borderRadius: 4, fontWeight: 700, verticalAlign: "middle" }}>🔍 실시간 뉴스 반영</span>}
                  </div>}
                  <div style={{ whiteSpace: "pre-wrap" }}>{d?.answer}</div>
                  {d?.conditions?.length > 0 && (<div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>{d.conditions.map((c, j) => <div key={j} style={{ fontSize: 12, color: T.yellow, marginTop: 4 }}>⟐ {c}</div>)}</div>)}
                  {d?.checkpoint && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>◎ 다음 체크: {d.checkpoint}</div>}
                </div>);
              })()}
            </div>
          </div>
        ))}
        {loading && (<div style={{ display: "flex", justifyContent: "flex-start" }}><div className="breathing-bg" style={{ padding: "14px 20px", borderRadius: 14, color: T.accent, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}><span className="pulse-anim">✨</span> {loadingText}</div></div>)}
        <div ref={bottom} />
      </div>
      {/* Sprint 12: 웹검색 상태 안내 */}
      {plan === "core" || plan === "pro" ? (
        <div style={{ fontSize: 11, color: T.cyan, padding: "6px 0", display: "flex", alignItems: "center", gap: 6 }}>
          <span>🔍</span> 실시간 웹 검색이 활성화되어 있어요
        </div>
      ) : (
        <div style={{ fontSize: 11, color: T.textMuted, padding: "8px 12px", background: T.accent + "08", borderRadius: 8, marginTop: 8 }}>
          🔒 Core 이상에서 실시간 뉴스가 반영된 답변을 받을 수 있어요
        </div>
      )}

      {/* Sprint 13-C: 시장 추천 질문 칩 */}
      {(() => {
        const MARKET_CHIPS = {
          MARKET_STATUS: ["내 포트폴리오에 영향 있어?", "오늘 특이 이슈 있어?"],
          SECTOR:        ["관련 섹터 전망은?", "관련 업종 흐름은?"],
          CONCEPT:       ["더 쉽게 설명해줘", "역사적으로 비슷한 적 있어?"],
          FOLLOW_UP:     ["핵심 근거 1개만 더 알려줘", "다른 관점은?"],
          STRATEGY:      ["지금 전략 유지가 맞을까?", "현금 비중 늘려야 할까?"]
        };
        const classifyMarket = (q) => {
          if (!q) return "MARKET_STATUS";
          if (/섹터|업종|산업|테마/.test(q)) return "SECTOR";
          if (/뭐야|뜻이|무슨말|의미/.test(q)) return "CONCEPT";
          if (/전략|매수|매도|비중|타이밍|현금/.test(q)) return "STRATEGY";
          if (q.length < 20 || /그게|그럼|왜/.test(q)) return "FOLLOW_UP";
          return "MARKET_STATUS";
        };
        const MARKET_ROTATION = { MARKET_STATUS: "STRATEGY", SECTOR: "MARKET_STATUS", CONCEPT: "FOLLOW_UP", FOLLOW_UP: "CONCEPT", STRATEGY: "MARKET_STATUS" };
        const lastUser = [...msgs].reverse().find(m => m.role === "user")?.text;
        const recentUserMsgs = msgs.filter(m => m.role === "user").slice(-3).map(m => m.text);
        const recentMTypes = msgs.filter(m => m.role === "user").slice(-2).map(m => classifyMarket(m.text));
        let mMode = classifyMarket(lastUser);
        if (recentMTypes.length >= 2 && recentMTypes.every(t => t === mMode)) mMode = MARKET_ROTATION[mMode] || "MARKET_STATUS";
        const chips = !loading && msgs.length > 0 && msgs[msgs.length - 1]?.role === "ai"
          ? (MARKET_CHIPS[mMode] || MARKET_CHIPS.MARKET_STATUS).filter(c => !recentUserMsgs.some(r => r.includes(c.slice(0, 6))))
          : [];
        return chips.length > 0 ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, animation: "chipFadeIn 0.25s ease-out" }}>
            {chips.map((chip, i) => (
              <div key={i} onClick={() => send(chip)} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${T.accent}40`, background: T.accent + "10", fontSize: 12, color: T.accent, fontWeight: 600, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}
                onMouseEnter={e => { e.currentTarget.style.background = T.accent + "25"; e.currentTarget.style.borderColor = T.accent; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.accent + "10"; e.currentTarget.style.borderColor = T.accent + "40"; }}>
                {chip}
              </div>
            ))}
          </div>
        ) : null;
      })()}

      <div style={{ display: "flex", gap: 8, marginTop: 8, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <Input style={{ flex: 1 }} placeholder="시장에 대해 궁금한 걸 물어보세요..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} />
        <Btn variant="primary" onClick={() => send()} disabled={loading} style={{ opacity: loading ? 0.5 : 1 }}>전송</Btn>
      </div>
    </div>
  );
}
